import { useFetcher } from 'react-router';
import { useEffect, useMemo, useReducer, useState } from 'react';
import { tgProtectedResourceLoader } from '~/shell/utils/authUtil.server';
import { useBasePath } from '~/common/hooks/useBasePath';
import { fetchDeliveryCalendar } from '~/features/cart/cartApi.server';
import invariant from 'tiny-invariant';
import type {
  IDeliveryCalendarDtoData,
  IDeliveryDateDtoData,
  IDeliveryDateWarningDtoData
} from '~/dtos/ProdegaApiWebshop-api-typings-generated';
import { chooseDeliveryDate } from '../../actions/cartActions';
import { format } from 'date-fns';
import { useCartIsBusy } from '../../state-hooks/useCartIsBusy';
import { useCartSummary } from '../../state-hooks/useCartSummary';

export const loader = tgProtectedResourceLoader(async ({ params, api }) => {
  const { weekoffset } = params;
  invariant(weekoffset, 'weekoffset value is required');

  const calendar = await fetchDeliveryCalendar(api, Number.parseInt(weekoffset, 10));

  return { calendar: calendar };
});

export function useDeliveryCalendar() {
  const { load, data, state } = useFetcher<typeof loader>();
  const [currentWeekOffset, setCurrentWeekOffset] = useState(0);
  const [date, setDate] = useState<Date>();
  const [deliveryDateWarning, setDeliveryDateWarning] = useState<IDeliveryDateWarningDtoData>();
  const { desiredDeliveryDate } = useCartSummary();
  const { path } = useBasePath();
  const { cartIsChangingDeliveryDate, cartIsChangingDeliveryDateExpired } = useCartIsBusy();
  const url = `${path}/resources/cart/deliverycalendar/`;
  const weeks = 6;
  const [, forceRender] = useReducer(renderCount => renderCount + 1, 0);

  const selectedDate = desiredDeliveryDate ? new Date(desiredDeliveryDate) : new Date();

  useEffect(() => {
    load(url + currentWeekOffset).then(() => {
      // Szenario Position-Submission ohne existierenden Warenkorb:
      // Die Komponente `CartDialogDeliveryCalendar` rendert immer nur den Spinner ... dieser Hack löst das Problem ...
      // Keine Ahnung, warum das in dem Szenario nicht funktioniert ... ein Verdacht wäre, dass in dem Szenario
      // d.h. auf der CreateOrderRoute das Hinzufügen einer Warenkorb-Position aus einem Effect ausgelöst wird.
      // Hier haben wir dann einen zweiten "verschachtelten" Effect welcher das `load` auslöst ... vielleicht gibt das Probleme ...
      forceRender();
    });
  }, [currentWeekOffset, load, path, url]);

  const nextMonth = () => {
    setCurrentWeekOffset(prevState => prevState + weeks);
  };

  const reset = () => {
    setCurrentWeekOffset(0);
  };

  const previousMonth = () => {
    setCurrentWeekOffset(prevState => prevState - weeks);
  };

  const onDateSelected = (deliveryDate: IDeliveryDateDtoData) => {
    setDate(deliveryDate.date);
    if (deliveryDate.deliveryDateWarning) {
      setDeliveryDateWarning(deliveryDate.deliveryDateWarning);
    } else {
      sendDeliveryDateChange(deliveryDate.date);
    }
  };

  const sendDeliveryDateChange = (date: Date) => {
    setDeliveryDateWarning(undefined);
    const formattedDate = format(date, 'yyyy-MM-dd');
    chooseDeliveryDate(formattedDate);
  };

  const abortSundayWarning = () => {
    setDeliveryDateWarning(undefined);
    setDate(selectedDate); // set date back to initial value
  };

  const confirmSundayWarning = () => {
    if (date) {
      sendDeliveryDateChange(date);
    }
  };

  const isLoading = useMemo(() => state === 'loading', [state]);

  return {
    isLoading,
    calendarData: data?.calendar as IDeliveryCalendarDtoData,
    nextMonth,
    previousMonth,
    onDateSelected,
    abortSundayWarning,
    confirmSundayWarning,
    deliveryDateWarning,
    selectedDate,
    cartIsChangingDeliveryDate,
    cartIsChangingDeliveryDateExpired,
    reset
  };
}
